import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Toast from '../components/Toast';
import { apiUrl, parseApiResponse } from '../utils/api';

export default function LivreurLivePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const code = (params.get('code') || '').trim();
  const token = (params.get('token') || '').trim();

  const [started, setStarted] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [lastSent, setLastSent] = useState(null);
  const [pos, setPos] = useState(null);

  const watchIdRef = useRef(null);
  const aliveRef = useRef(true);

  const canStart = useMemo(() => !!code && !!token, [code, token]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      try {
        if (watchIdRef.current !== null && navigator.geolocation) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
      } catch {
        // ignore
      }
    };
  }, []);

  const postLocation = async (p) => {
    if (!p || !canStart) return;
    if (sending) return;
    setSending(true);
    setErr('');
    try {
      const payload = {
        code,
        token,
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        accuracy: p.coords.accuracy,
        heading: p.coords.heading,
        speed: p.coords.speed,
      };

      const res = await fetch(apiUrl('public/colis/location'), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const out = await parseApiResponse(res);
      if (!out.ok) {
        const msg = out?.json?.message || out?.text || 'Erreur serveur';
        throw new Error(String(msg));
      }

      if (!aliveRef.current) return;
      setLastSent(out?.json?.location || null);
    } catch (e) {
      if (!aliveRef.current) return;
      setErr(e?.message || 'Erreur');
    } finally {
      if (aliveRef.current) setSending(false);
    }
  };

  const start = async () => {
    if (!canStart) {
      setErr('Lien invalide (code/token manquant)');
      return;
    }
    if (!navigator.geolocation) {
      setErr('Géolocalisation non supportée sur cet appareil');
      return;
    }

    setErr('');
    setStarted(true);

    try {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (p) => {
          if (!aliveRef.current) return;
          setPos(p);
          postLocation(p);
        },
        (e) => {
          if (!aliveRef.current) return;
          setErr(e?.message || 'Permission GPS refusée');
        },
        {
          enableHighAccuracy: true,
          maximumAge: 3000,
          timeout: 15000,
        }
      );
    } catch {
      setErr('Impossible de démarrer la géolocalisation');
    }
  };

  const stop = () => {
    try {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    } catch {
      // ignore
    }
    watchIdRef.current = null;
    setStarted(false);
  };

  const lat = pos?.coords?.latitude;
  const lng = pos?.coords?.longitude;
  const gmaps = (lat != null && lng != null) ? `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}` : '';

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '40px 20px' }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#4F46E5', fontWeight: 600, fontSize: 14,
          marginBottom: 22, display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', padding: 0,
        }}>
          ← Retour
        </button>

        <div style={{
          background: 'white',
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 6 }}>📍 Partage de position (livreur)</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
            Colis: <span style={{ fontFamily: 'monospace', fontWeight: 900, color: '#4F46E5' }}>{code || '—'}</span>
          </div>

          {!!err && (
            <div style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 14,
              padding: '10px 12px',
              color: '#991B1B',
              fontWeight: 800,
              marginBottom: 14,
              fontSize: 13,
            }}>
              {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {!started ? (
              <button
                type="button"
                onClick={start}
                disabled={!canStart}
                style={{
                  padding: '12px 16px',
                  background: canStart ? 'linear-gradient(135deg, #4F46E5, #8B5CF6)' : '#e2e8f0',
                  color: canStart ? 'white' : '#94a3b8',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 900,
                  cursor: canStart ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                ▶️ Démarrer
              </button>
            ) : (
              <button
                type="button"
                onClick={stop}
                style={{
                  padding: '12px 16px',
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 900,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                ⏹️ Arrêter
              </button>
            )}

            <button
              type="button"
              onClick={() => pos && postLocation(pos)}
              disabled={!started || !pos || sending}
              style={{
                padding: '12px 16px',
                background: (!started || !pos || sending) ? '#e2e8f0' : '#EEF2FF',
                color: (!started || !pos || sending) ? '#94a3b8' : '#3730A3',
                border: 'none',
                borderRadius: 12,
                fontWeight: 900,
                cursor: (!started || !pos || sending) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {sending ? '...' : '📡 Envoyer maintenant'}
            </button>

            {!!gmaps && (
              <a
                href={gmaps}
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: '12px 16px',
                  background: '#F1F5F9',
                  color: '#0f172a',
                  borderRadius: 12,
                  fontWeight: 900,
                  textDecoration: 'none',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                🗺️ Ouvrir Maps
              </a>
            )}
          </div>

          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: '#334155', marginBottom: 6 }}>Dernière position</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              {lat != null && lng != null ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '—'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
              {pos?.coords?.accuracy ? `Précision: ${Math.round(pos.coords.accuracy)} m` : ''}
              {pos?.timestamp ? ` · ${new Date(pos.timestamp).toLocaleString()}` : ''}
            </div>

            {!!lastSent?.ts && (
              <div style={{ fontSize: 12, color: '#475569', marginTop: 10, fontWeight: 800 }}>
                ✅ Dernier envoi serveur: {new Date(lastSent.ts).toLocaleString()}
              </div>
            )}
          </div>

          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, lineHeight: 1.4 }}>
            Important: laisser cette page ouverte sur le téléphone pendant la livraison.
          </div>
        </div>
      </main>

      <Toast />
    </div>
  );
}
